import { useMemo, useRef } from 'react'
import type { MutableRefObject, ReactNode } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { RoundedBox } from '@react-three/drei'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { Merkplaat } from './onderdelen'
import type { TruckKleuren } from './onderdelen'
import { Blok, PuWiel, Tussen, useMaticMaterialen } from './bouwdelen'
import type { Mat } from './bouwdelen'
import { useAnimatie } from '../../scene/store'

/**
 * L-MATIC core — autonome palletstapelaar, serie 1195-01.
 *
 * ## Waar de vorm vandaan komt
 *
 * Het VDI-typeblad (`ds_l-matic_core_1195-01`) met de maattekening in zij-,
 * voor- en bovenaanzicht, en de studiobeelden van Linde in
 * `docs/referenties/l-matic-core/`. Maten en keuzes staan in
 * `docs/lmatic-maten.md`.
 *
 * ## De opbouw
 *
 *  - **aandrijfunit** (−z): een rode kast van 887 breed en 1,21 m hoog, kort
 *    (0,40 m tot de mast) met een grote ronding van de achterkant naar het
 *    dak. Op de achterkant een donkergrijze V die van het display naar
 *    beneden smaller wordt; onderin vier hoekblokken met de veiligheids-
 *    scanners (360°), opzij de laadcontacten;
 *  - **sensorzuil**: een zwarte kolom uit het dak, met bovenin een T met de
 *    navigatielaser, de noodstop, een 3D-camera en een statuslamp — tot
 *    1990 mm;
 *  - **polycarbonaat kap** over de mastkop, tussen zuil en mast;
 *  - **mast**: duplex, 1415 ingetrokken, 264 vrije heffing, heft 1844;
 *  - **vorkenwagen** met een afgeronde plaat en een smal lastrek;
 *  - **vorken** 55 × 182 × 1250 met vensters boven de lastrollen: ze liggen
 *    gezakt over de wielarmen, die op de vloer blijven als de vorken heffen.
 *
 * ## Oorsprong
 *
 * Het midden van de lengte (l1 1785), 0,9 m voor de achterkant. `s(mm)`
 * rekent een afstand vanaf de achterkant om.
 */

/** Afstand in mm vanaf de achterkant → z in meters vanaf de oorsprong. */
const s = (mm: number) => mm / 1000 - 0.9

/** Typeblad 1195-01. */
const B1 = 0.887
const H1 = 1.415
const H2 = 0.264
const H3 = 1.844
const H4 = 2.364
const H13 = 0.086
const VORK = { s: 0.055, e: 0.182, l: 1.25 }
const B5 = 0.56
const B3 = 0.78
/** Vorkrug (l2) en lastrollen (achterkant tot aandrijfas 186,9 + wielbasis y 1013). */
const L2 = 535
const AS = 187
const LASTROL = AS + 1013

/** Bovenkant van de kast, en de ronding van achterkant naar dak. */
const KAST_H = 1.21
const RONDING = 0.27
const KAST_ONDER = 0.075
/** De kast loopt tot de mast; de mast tot de vorkrug. */
const MAST_Z0 = s(400)
const MAST_Z1 = s(525)
/**
 * Polycarbonaat kap: van achter de zuil over de mastkop. Het dak loopt tot
 * vlak voor de binnenmast, de lastkant is open: daar gaan binnenmast en
 * lastrek doorheen als hij heft.
 */
const KAP = { z0: s(262), z1: s(520), dak: s(410), y0: 1.34, y1: 1.64 }
/** Sensorzuil. */
const ZUIL = { z0: s(165), z1: s(255), top: 1.8 }

/** Vorkblad gezakt: de bovenkant op h13. */
const VORK_TOP = H13
/** Uitschuiving van de binnenmast bij volle heffing: h4 − h1. */
const MAST_UIT = H4 - H1

/* ------------------------------------------------------------------ vormen */

/**
 * Zijprofiel van de kast in (z vanaf achter, y): recht omhoog, dan de grote
 * ronding naar het dak, vlak tot de mast.
 */
function kastVorm(b: number) {
  // `b` naar binnen: de afschuining van de extrusie legt hem er weer omheen.
  const v = new THREE.Shape()
  const r = RONDING - b
  const zMast = 0.4 - b
  const top = KAST_H - b
  v.moveTo(b, KAST_ONDER + b)
  v.lineTo(zMast, KAST_ONDER + b)
  v.lineTo(zMast, top - 0.02)
  v.quadraticCurveTo(zMast, top, zMast - 0.02, top)
  v.lineTo(RONDING, top)
  v.absarc(RONDING, KAST_H - RONDING, r, Math.PI / 2, Math.PI, false)
  v.lineTo(b, KAST_ONDER + b)
  return v
}

function kastGeometrie() {
  const b = 0.012
  const g = new THREE.ExtrudeGeometry(kastVorm(b), {
    depth: B1 - 2 * b, bevelEnabled: true, bevelThickness: b, bevelSize: b, bevelSegments: 3, curveSegments: 24,
  })
  // Vorm in (z, y), extrusie langs +z: een kwartslag om y legt de vormas op
  // z en de extrusie op −x. Dan naar het midden, en z vanaf de oorsprong.
  g.rotateY(-Math.PI / 2)
  g.translate(B1 / 2 - b, 0, s(0))
  // Zacht over de ronding: de extrusie is facetgewijs belicht, en op de
  // lak zie je elk facet als een streep.
  g.deleteAttribute('uv')
  const glad = mergeVertices(g, 1e-4)
  g.dispose()
  glad.computeVertexNormals()
  return glad
}

/**
 * Een band over het profiel van de achterkant: van y0 recht omhoog, dan over
 * de ronding tot hoek θ1 (0 = dak, π/2 = achterkant). `breedte(t)` geeft de
 * halve breedte over de lengte van de band (t = 0..1), `uit` hoe ver hij
 * voor de kast ligt.
 */
function achterBand(y0: number, theta1: number, breedte: (t: number) => number, uit: number) {
  const r = RONDING + uit
  const cz = s(0) + RONDING
  const cy = KAST_H - RONDING
  const punten: { z: number; y: number }[] = []
  for (let i = 0; i <= 8; i++) punten.push({ z: s(0) - uit, y: y0 + ((cy - y0) * i) / 8 })
  for (let i = 1; i <= 16; i++) {
    const a = Math.PI / 2 - ((Math.PI / 2 - theta1) * i) / 16
    punten.push({ z: cz - r * Math.sin(a), y: cy + r * Math.cos(a) })
  }
  const pos: number[] = []
  const idx: number[] = []
  punten.forEach((p, i) => {
    const w = breedte(i / (punten.length - 1))
    pos.push(-w, p.y, p.z, w, p.y, p.z)
    if (i > 0) {
      const a = (i - 1) * 2
      idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3)
    }
  })
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setIndex(idx)
  g.computeVertexNormals()
  return g
}

/**
 * Een band over alleen de ronding, van hoek a0 tot a1 (0 = dak), met halve
 * breedte `b`, `uit` voor de kast: het display en zijn rand volgen de ronding,
 * zoals op de beelden.
 */
function boogBand(a0: number, a1: number, b: number, uit: number) {
  const r = RONDING + uit
  const cz = s(0) + RONDING
  const cy = KAST_H - RONDING
  const pos: number[] = []
  const idx: number[] = []
  const n = 10
  for (let i = 0; i <= n; i++) {
    const a = a1 - ((a1 - a0) * i) / n
    pos.push(-b, cy + r * Math.cos(a), cz - r * Math.sin(a), b, cy + r * Math.cos(a), cz - r * Math.sin(a))
    if (i > 0) {
      const j = (i - 1) * 2
      idx.push(j, j + 2, j + 1, j + 1, j + 2, j + 3)
    }
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setIndex(idx)
  g.computeVertexNormals()
  return g
}

/**
 * Een vork in bovenaanzicht (x, z vanaf de vorkrug): 182 breed, een punt die
 * de laatste 13 cm smaller wordt, en het venster boven de lastrollen.
 */
function vorkVorm() {
  const v = new THREE.Shape()
  const h = VORK.e / 2
  const l = VORK.l
  v.moveTo(-h, 0)
  v.lineTo(h, 0)
  v.lineTo(h, l - 0.13)
  v.lineTo(h - 0.03, l - 0.012)
  v.quadraticCurveTo(h - 0.034, l, h - 0.05, l)
  v.lineTo(-h + 0.05, l)
  v.quadraticCurveTo(-h + 0.034, l, -h + 0.03, l - 0.012)
  v.lineTo(-h, l - 0.13)
  v.lineTo(-h, 0)
  // Venster boven de twee lastrollen (tandem, Ø 85).
  const r0 = (LASTROL - 90 - L2) / 1000
  const r1 = (LASTROL + 90 - L2) / 1000
  const gat = new THREE.Path()
  gat.moveTo(-0.05, r0)
  gat.lineTo(-0.05, r1)
  gat.lineTo(0.05, r1)
  gat.lineTo(0.05, r0)
  gat.lineTo(-0.05, r0)
  v.holes.push(gat)
  return v
}

function vorkGeometrie() {
  const g = new THREE.ExtrudeGeometry(vorkVorm(), {
    depth: VORK.s - 0.008, bevelEnabled: true, bevelThickness: 0.004, bevelSize: 0.004, bevelSegments: 2,
    curveSegments: 6,
  })
  // Vorm in (x, z), extrusie → omlaag: een kwartslag om x legt vormas y op z
  // en de extrusie op −y. De bovenkant komt op y = 0.
  g.rotateX(Math.PI / 2)
  g.translate(0, -0.004, 0)
  return g
}

/** Afgeronde plaat van de vorkenwagen, in (x, y). */
function plaatVorm(b: number, h: number, r: number) {
  const v = new THREE.Shape()
  v.moveTo(-b / 2, 0)
  v.lineTo(b / 2, 0)
  v.lineTo(b / 2, h - r)
  v.quadraticCurveTo(b / 2, h, b / 2 - r, h)
  v.lineTo(-b / 2 + r, h)
  v.quadraticCurveTo(-b / 2, h, -b / 2, h - r)
  v.lineTo(-b / 2, 0)
  return v
}

/* ------------------------------------------------------------------ materialen */

function useEigenMaterialen() {
  return useMemo(() => {
    // Polycarbonaat: licht, grijzig, doorschijnend. Hij spiegelt, dat is
    // waaraan je hem herkent.
    const polycarbonaat = new THREE.MeshPhysicalMaterial({
      // Op de draaitafel melkig lichtgrijs: je ziet de mastkop er vaag door.
      color: '#dde1e5', roughness: 0.2, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.05,
      transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide,
    })
    const vplaat = new THREE.MeshPhysicalMaterial({
      color: '#383b41', roughness: 0.42, metalness: 0.12, clearcoat: 0.25, clearcoatRoughness: 0.35,
      side: THREE.DoubleSide,
    })
    const hoekblok = new THREE.MeshStandardMaterial({ color: '#4a4d53', roughness: 0.5, metalness: 0.2 })
    const koper = new THREE.MeshStandardMaterial({ color: '#b8834e', roughness: 0.32, metalness: 0.85 })
    const display = new THREE.MeshStandardMaterial({
      color: '#0e1a24', emissive: '#27465e', emissiveIntensity: 0.35, roughness: 0.15,
    })
    const groen = new THREE.MeshStandardMaterial({ color: '#7ef08e', emissive: '#48d060', emissiveIntensity: 0.35 })
    const slang = new THREE.MeshStandardMaterial({ color: '#3d8a4c', roughness: 0.5 })
    const waarschuwing = new THREE.MeshStandardMaterial({ color: '#f0c419', roughness: 0.5, side: THREE.DoubleSide })
    const aluminium = new THREE.MeshStandardMaterial({ color: '#8b9096', roughness: 0.38, metalness: 0.6 })
    return { polycarbonaat, vplaat, hoekblok, koper, display, groen, slang, waarschuwing, aluminium }
  }, [])
}
type Eigen = ReturnType<typeof useEigenMaterialen>

/* ------------------------------------------------------------------ aandrijfunit */

/** Grijs vlak onder op de zijkant, van de laadcontacten tot de mast; in (−k·z, y). */
function zijvlakVorm(k: number) {
  const v = new THREE.Shape()
  const zz = (z: number) => -k * z
  v.moveTo(zz(s(110)), 0.3)
  v.lineTo(zz(s(110)), 0.52)
  v.lineTo(zz(s(250)), 0.64)
  v.lineTo(zz(s(398)), 0.64)
  v.lineTo(zz(s(398)), 0.3)
  v.lineTo(zz(s(110)), 0.3)
  return v
}

function Aandrijfunit({ m, e }: { m: Mat; e: Eigen }) {
  const kast = useMemo(() => kastGeometrie(), [])
  const zijvlak = useMemo(() => [-1, 1].map((k) => new THREE.ShapeGeometry(zijvlakVorm(k))), [])
  // De V: onderaan 16 cm breed, boven de achterkant 52, en zo over de
  // ronding door tot onder het dak.
  const v = useMemo(
    () => achterBand(0.23, 0.32, (t) => (t < 0.33 ? 0.08 + (0.26 - 0.08) * (t / 0.33) : 0.26 + 0.02 * ((t - 0.33) / 0.67)), 0.004),
    [],
  )
  // Display op de ronding, met een donkere rand eromheen: allebei banden die
  // de ronding volgen. De knoppenrij onderaan de rand.
  const rand = useMemo(() => boogBand(0.5, 1.36, 0.185, 0.006), [])
  const scherm = useMemo(() => boogBand(0.62, 1.08, 0.1, 0.009), [])
  const knop = 1.24
  const kz = s(0) + RONDING - (RONDING + 0.009) * Math.sin(knop)
  const ky = KAST_H - RONDING + (RONDING + 0.009) * Math.cos(knop)
  return (
    <group>
      {/* Bodemplaat en stootrand, zwart. */}
      <Tussen a={[-0.44, 0.017, s(10)]} b={[0.44, KAST_ONDER + 0.01, s(470)]} m={m.zwart} />
      <mesh geometry={kast} material={m.lakRood} castShadow receiveShadow />
      <mesh geometry={v} material={e.vplaat} castShadow />

      <mesh geometry={rand} material={m.kunststof} />
      <mesh geometry={scherm} material={e.display} />
      <group position={[0, ky, kz]} rotation={[Math.PI / 2 - knop, Math.PI, 0]}>
        {[-0.11, -0.066, -0.022, 0.022, 0.066, 0.11].map((x, i) => (
          <mesh key={x} position={[x, 0, 0]} rotation={[Math.PI / 2, 0, 0]}
            material={i === 5 ? m.noodstop : m.zwart}>
            <cylinderGeometry args={[0.012, 0.012, 0.008, 12]} />
          </mesh>
        ))}
      </group>
      <Merkplaat positie={[0, 0.74, s(0) - 0.008]} rotatie={[0, Math.PI, 0]} breedte={0.26} type="L-MATIC" beugel={false} />

      {/* Rode stootband onderin met bouten, en de vier hoekblokken met de
          veiligheidsscanners. */}
      {[-0.1, 0.1].map((x) => [0.12, 0.19].map((y) => (
        <mesh key={`${x}${y}`} position={[x, y, s(0) - 0.004]} rotation={[Math.PI / 2, 0, 0]} material={m.zwart}>
          <cylinderGeometry args={[0.009, 0.009, 0.006, 8]} />
        </mesh>
      )))}
      {[-1, 1].map((k) => [s(0) + 0.095, s(322)].map((z, i) => (
        <group key={`${k}${i}`} position={[k * (B1 / 2 - 0.075), 0.18, z]}>
          <Blok p={[0, 0, 0]} s={[0.155, 0.23, i === 0 ? 0.19 : 0.15]} m={e.hoekblok} />
          {/* Scannervenster op de hoek, 45° naar buiten. */}
          <mesh position={[k * 0.06, -0.03, (i === 0 ? -0.07 : 0.05)]}
            rotation={[0, (i === 0 ? Math.PI : 0) + k * (i === 0 ? -1 : 1) * Math.PI / 4, 0]} material={m.zwart}>
            <boxGeometry args={[0.1, 0.06, 0.012]} />
          </mesh>
          {i === 1 && (
            <mesh position={[k * 0.0785, 0.05, 0]} rotation={[0, k * Math.PI / 2, 0]} material={m.geel}>
              <planeGeometry args={[0.04, 0.03]} />
            </mesh>
          )}
        </group>
      )))}

      {/* Opzij: grijs vlak onderaan bij de mast, ventilatiesleuven boven,
          en aan de −x-kant de laadcontacten. */}
      {[-1, 1].map((k) => (
        <group key={k}>
          <mesh geometry={zijvlak[k < 0 ? 0 : 1]} position={[k * (B1 / 2 + 0.002), 0, 0]}
            rotation={[0, k * Math.PI / 2, 0]} material={e.vplaat} />
          {[0, 1, 2, 3, 4].map((i) => (
            <Blok key={i} p={[k * (B1 / 2 + 0.001), 1.03 + i * 0.022, s(262)]} s={[0.004, 0.008, 0.1]} m={m.zwart} schaduw={false} />
          ))}
          {/* Noodstop aan elke kant. */}
          <mesh position={[k * (B1 / 2 + 0.012), 0.9, s(360)]} rotation={[0, 0, Math.PI / 2]} material={m.noodstop}>
            <cylinderGeometry args={[0.022, 0.018, 0.02, 14]} />
          </mesh>
        </group>
      ))}
      <group position={[-(B1 / 2 + 0.004), 0.44, s(190)]}>
        <Blok p={[0, 0, 0]} s={[0.012, 0.14, 0.17]} m={m.zwart} />
        {[-0.035, 0.035].map((y) => (
          <Blok key={y} p={[-0.006, y, 0]} s={[0.006, 0.034, 0.15]} m={e.koper} schaduw={false} />
        ))}
      </group>

      {/* Aandrijfwiel en zwenkwiel, b10 = 426,5 uit elkaar. */}
      <PuWiel p={[-0.213, 0.115, s(AS)]} r={0.115} b={0.09} m={m} />
      <PuWiel p={[0.213, 0.07, s(AS)]} r={0.07} b={0.054} m={m} />
    </group>
  )
}

/* ------------------------------------------------------------------ sensorzuil en kap */

function Sensorzuil({ m, e }: { m: Mat; e: Eigen }) {
  const zm = (ZUIL.z0 + ZUIL.z1) / 2
  return (
    <group>
      {/* Op de draaitafel is de zuil grijs gespoten, geen zwart, en de T
          blank aluminium met boutkoppen. */}
      <Tussen a={[-0.075, KAST_H - 0.06, ZUIL.z0]} b={[0.075, ZUIL.top, ZUIL.z1]} m={m.mast} />
      {/* De T bovenin, met lampen en camera's aan de uiteinden. */}
      <Tussen a={[-0.22, ZUIL.top, ZUIL.z0 - 0.04]} b={[0.22, ZUIL.top + 0.09, ZUIL.z1 + 0.01]} m={e.aluminium} />
      <Tussen a={[-0.09, ZUIL.top + 0.09, zm - 0.07]} b={[0.09, ZUIL.top + 0.115, zm + 0.07]} m={m.mast} />
      {/* Navigatielaser en noodstop: tot 1990. */}
      <mesh position={[0, ZUIL.top + 0.15, zm]} material={m.zwart} castShadow>
        <cylinderGeometry args={[0.05, 0.055, 0.07, 18]} />
      </mesh>
      <mesh position={[0, ZUIL.top + 0.15, zm]} material={m.glas}>
        <cylinderGeometry args={[0.052, 0.052, 0.025, 18]} />
      </mesh>
      {/* Noodstop bovenop: zwarte kraag, rode paddenstoel. */}
      <mesh position={[0, ZUIL.top + 0.19, zm]} material={m.zwart}>
        <cylinderGeometry args={[0.028, 0.03, 0.022, 16]} />
      </mesh>
      <mesh position={[0, ZUIL.top + 0.2, zm]} material={m.noodstop}>
        <sphereGeometry args={[0.03, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
      </mesh>
      {/* 3D-camera (blauw) aan +x, statuslamp aan −x; ze kijken naar achter. */}
      <group position={[0.2, ZUIL.top - 0.03, ZUIL.z0 - 0.03]}>
        <RoundedBox args={[0.07, 0.06, 0.07]} radius={0.012} smoothness={2} material={m.chassis} />
        <mesh position={[0, 0, -0.036]} rotation={[Math.PI / 2, 0, 0]} material={m.lens}>
          <cylinderGeometry args={[0.022, 0.022, 0.006, 16]} />
        </mesh>
      </group>
      <mesh position={[-0.2, ZUIL.top + 0.045, ZUIL.z0 - 0.02]} material={e.groen}>
        <boxGeometry args={[0.05, 0.035, 0.05]} />
      </mesh>
      {/* Tweede camera schuin omlaag, onder aan de T. */}
      <group position={[0.1, ZUIL.top - 0.08, ZUIL.z0 - 0.02]} rotation={[0.5, 0, 0]}>
        <Blok p={[0, 0, 0]} s={[0.06, 0.05, 0.05]} m={m.chassis} />
        <mesh position={[0, 0, -0.026]} rotation={[Math.PI / 2, 0, 0]} material={m.glas}>
          <cylinderGeometry args={[0.015, 0.015, 0.004, 12]} />
        </mesh>
      </group>
    </group>
  )
}

/** Polycarbonaat kap over de mastkop, op een zwart frame boven de kast. */
function Kap({ m, e }: { m: Mat; e: Eigen }) {
  const { z0, z1, dak, y0, y1 } = KAP
  const b = B1 / 2 - 0.01
  const zm = (z0 + z1) / 2
  const d = z1 - z0
  const h = y1 - y0
  const driehoek = useMemo(() => new THREE.CircleGeometry(0.024, 3), [])
  return (
    <group>
      {/* Zwart frame tussen kast en kap, alleen boven de kast: boven de mast
          moet het lastrek erdoor. */}
      <Tussen a={[-b, KAST_H, z0 + 0.02]} b={[b, y0, s(398)]} m={m.chassis} />
      {/* Dak, achterkant en zijkanten; de lastkant is open. */}
      <mesh position={[0, y1, (z0 + dak) / 2]} rotation={[-Math.PI / 2, 0, 0]} material={e.polycarbonaat}>
        <planeGeometry args={[2 * b, dak - z0]} />
      </mesh>
      <mesh position={[0, y0 + h / 2, z0]} material={e.polycarbonaat}>
        <planeGeometry args={[2 * b, h]} />
      </mesh>
      {[-1, 1].map((k) => (
        <mesh key={`x${k}`} position={[k * b, y0 + h / 2, zm]} rotation={[0, Math.PI / 2, 0]} material={e.polycarbonaat}>
          <planeGeometry args={[d, h]} />
        </mesh>
      ))}
      {[-1, 1].map((kx) => [z0, z1].map((z) => (
        <Blok key={`${kx}${z}`} p={[kx * b, y0 + h / 2, z]} s={[0.012, h, 0.012]} m={m.mast} schaduw={false} />
      )))}
      {[-1, 1].map((kx) => (
        <Blok key={kx} p={[kx * b, y1, zm]} s={[0.012, 0.012, d]} m={m.mast} schaduw={false} />
      ))}
      {/* Waarschuwingsdriehoeken op de achterkant. */}
      {[-1, 1].map((k) => (
        <mesh key={k} geometry={driehoek} position={[k * (b - 0.07), y1 - 0.05, z0 - 0.002]}
          rotation={[0, Math.PI, Math.PI / 2]} material={e.waarschuwing} />
      ))}
    </group>
  )
}

/* ------------------------------------------------------------------ mast en vorken */

function Mast({ m, binnen, cilinder }: {
  m: Mat
  binnen: MutableRefObject<THREE.Group | null>
  cilinder: MutableRefObject<THREE.Group | null>
}) {
  const zm = (MAST_Z0 + MAST_Z1) / 2
  return (
    <group>
      {/* Buitenmast: twee profielen aan de buitenkant, onder en boven een
          dwarsbalk. */}
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.36, 0.03, MAST_Z0]} b={[k * 0.415, H1, MAST_Z1 - 0.02]} m={m.mast} />
      ))}
      <Tussen a={[-0.415, 0.03, MAST_Z0]} b={[0.415, 0.1, MAST_Z0 + 0.05]} m={m.mast} />
      {/* Achterwand van de mast: vanaf de lastkant zie je geen lak maar staal. */}
      <Tussen a={[-0.36, 0.1, MAST_Z0]} b={[0.36, KAP.y0, MAST_Z0 + 0.012]} m={m.chassis} />
      <Tussen a={[-0.415, H1 - 0.07, MAST_Z0]} b={[0.415, H1, MAST_Z0 + 0.05]} m={m.mast} />
      {/* Hefcilinder in het midden, achter de vorkenwagen. */}
      <mesh position={[0, 0.1 + 0.6, MAST_Z0 + 0.04]} material={m.chassis} castShadow>
        <cylinderGeometry args={[0.035, 0.035, 1.2, 12]} />
      </mesh>
      <group ref={cilinder}>
        <mesh position={[0, 0.1 + 1.2 + 0.1, MAST_Z0 + 0.04]} material={m.vork}>
          <cylinderGeometry args={[0.022, 0.022, 0.2, 12]} />
        </mesh>
      </group>

      <group ref={binnen}>
        {[-1, 1].map((k) => (
          <Tussen key={k} a={[k * 0.3, 0.06, zm - 0.04]} b={[k * 0.355, H1 - 0.02, zm + 0.045]} m={m.binnen} />
        ))}
        <Tussen a={[-0.355, H1 - 0.09, zm - 0.04]} b={[0.355, H1 - 0.02, zm]} m={m.binnen} />
        {/* Hefkettingen. */}
        {[-0.1, 0.1].map((x) => (
          <Tussen key={x} a={[x - 0.01, 0.3, zm + 0.02]} b={[x + 0.01, H1 - 0.1, zm + 0.03]} m={m.zwart} schaduw={false} />
        ))}
      </group>
    </group>
  )
}

/** Rechthoek met ronde hoeken als pad, voor een omtrek of een gat. */
function afgerond(p: THREE.Path, x0: number, y0: number, x1: number, y1: number, r: number) {
  p.moveTo(x0 + r, y0)
  p.lineTo(x1 - r, y0)
  p.quadraticCurveTo(x1, y0, x1, y0 + r)
  p.lineTo(x1, y1 - r)
  p.quadraticCurveTo(x1, y1, x1 - r, y1)
  p.lineTo(x0 + r, y1)
  p.quadraticCurveTo(x0, y1, x0, y1 - r)
  p.lineTo(x0, y0 + r)
  p.quadraticCurveTo(x0, y0, x0 + r, y0)
  return p
}

/**
 * Lastrek: 440 × 640, een kader van 40 mm met zes vensters (twee kolommen,
 * drie rijen) — uit de draaitafel van Linde, frame 27. Dikte 24 mm met een
 * afronding, zodat elke rand een glimlijn heeft; de vorm staat in (x, y) en
 * de extrusie wijst naar −z (naar de mast).
 */
function lastrekGeometrie() {
  const B = 0.22
  const H = 0.64
  const rand = 0.04
  const vorm = afgerond(new THREE.Shape(), -B, 0, B, H, 0.05) as THREE.Shape
  const vw = (2 * B - 3 * rand) / 2
  const vh = (H - 4 * rand) / 3
  for (let c = 0; c < 2; c++) {
    for (let r = 0; r < 3; r++) {
      const x0 = -B + rand + c * (vw + rand)
      const y0 = rand + r * (vh + rand)
      vorm.holes.push(afgerond(new THREE.Path(), x0, y0, x0 + vw, y0 + vh, 0.028))
    }
  }
  const g = new THREE.ExtrudeGeometry(vorm, {
    depth: 0.014, bevelEnabled: true, bevelThickness: 0.005, bevelSize: 0.005, bevelSegments: 2, curveSegments: 6,
  })
  g.translate(0, 0, -0.019)
  return g
}

/**
 * Vorkenwagen: dwarsbalken tussen de mast (b3 = 780), de afgeronde plaat
 * voor de vorkhielen, het lastrek erboven, en de vorken. In het assenstelsel
 * van de gezakte wagen: de vorkbovenkant op h13.
 */
function Vorkenwagen({ m, e }: { m: Mat; e: Eigen }) {
  const vork = useMemo(() => vorkGeometrie(), [])
  const plaat = useMemo(() => new THREE.ExtrudeGeometry(plaatVorm(0.62, 0.42, 0.12), {
    depth: 0.02, bevelEnabled: true, bevelThickness: 0.006, bevelSize: 0.006, bevelSegments: 2, curveSegments: 8,
  }), [])
  const rek = useMemo(() => lastrekGeometrie(), [])
  const zv = s(L2)
  const x = B5 / 2 - VORK.e / 2
  return (
    <group>
      {[0.12, 0.4].map((y) => (
        <Tussen key={y} a={[-B3 / 2, y, zv - 0.027]} b={[B3 / 2, y + 0.08, zv - 0.012]} m={m.mast} />
      ))}
      <mesh geometry={plaat} position={[0, VORK_TOP - 0.01, zv - 0.028]} material={m.kunststof} castShadow />
      {/* Lastrek: één plaat met twee kolommen en drie rijen vensters, alles
          met ronde hoeken, zoals op de draaitafel van Linde. */}
      <mesh geometry={rek} position={[0, VORK_TOP + 0.41, zv - 0.004]} material={m.kunststof} castShadow />
      {/* Groene slang langs de wagen. */}
      <Tussen a={[0.16, VORK_TOP + 0.4, zv - 0.052]} b={[0.17, VORK_TOP + 1.0, zv - 0.042]} m={e.slang} schaduw={false} />

      {[-x, x].map((xx) => (
        <group key={xx}>
          <mesh geometry={vork} position={[xx, VORK_TOP, zv]} material={m.vork} castShadow receiveShadow />
          {/* Vorkhiel tegen de plaat. */}
          <Tussen a={[xx - VORK.e / 2 + 0.02, VORK_TOP - VORK.s, zv - 0.03]} b={[xx + VORK.e / 2 - 0.02, VORK_TOP + 0.05, zv]} m={m.vork} />
        </group>
      ))}
    </group>
  )
}

/**
 * Wielarmen: blijven op de vloer, met de lastrollen (tandem, Ø 85 × 65).
 * Gezakt liggen de vorken eroverheen en zie je de rollen door de vensters.
 */
function Wielarmen({ m }: { m: Mat }) {
  const x = B5 / 2 - VORK.e / 2
  return (
    <group>
      {[-x, x].map((xx) => (
        <group key={xx}>
          <Tussen a={[xx - 0.06, 0.012, s(460)]} b={[xx + 0.06, 0.028, s(1720)]} m={m.chassis} />
          {[-1, 1].map((k) => (
            <Tussen key={k} a={[xx + k * 0.06 - 0.006, 0.012, s(460)]} b={[xx + k * 0.06, 0.07, s(1720)]} m={m.chassis} />
          ))}
          {[LASTROL - 42, LASTROL + 42].map((z) => (
            <PuWiel key={z} p={[xx, 0.0425, s(z)]} r={0.0425} b={0.065} m={m} />
          ))}
        </group>
      ))}
    </group>
  )
}

/* ------------------------------------------------------------------ de machine */

export function LMatic({ kleuren, hef, stand, lading }: {
  kleuren: TruckKleuren
  /** 0..1: van gezakt tot volle heffing (h3 = 1844). */
  hef: MutableRefObject<number>
  /**
   * Vorkhoogte boven de laagste stand, in meters, per frame. Zonder dit volgt
   * hij `hef` — zo gebruiken de showroom en de vergelijking hem.
   */
  stand?: MutableRefObject<{ hoogte: number }>
  /** Wat er op de vorken ligt, in het stelsel van de vorkenwagen (`heenEnWeer.ts`). */
  lading?: ReactNode
}) {
  const m = useMaticMaterialen(kleuren)
  const e = useEigenMaterialen()
  const anim = useAnimatie()
  const wagen = useRef<THREE.Group>(null)
  const binnen = useRef<THREE.Group | null>(null)
  const cilinder = useRef<THREE.Group | null>(null)

  useFrame(() => {
    const h = anim.aan && anim.mast ? hef.current : 0
    const hoog = stand ? THREE.MathUtils.clamp(stand.current.hoogte, 0, H3) : THREE.MathUtils.clamp(h, 0, 1) * H3
    // Duplex met vrije heffing: tot h2 gaat alleen de wagen omhoog, daarna
    // schuift de binnenmast mee, tot h4 − h1 bij volle heffing.
    const uit = Math.max(0, hoog - H2) * (MAST_UIT / (H3 - H2))
    if (wagen.current) wagen.current.position.y = hoog
    if (binnen.current) binnen.current.position.y = uit
    if (cilinder.current) cilinder.current.position.y = uit
  })

  return (
    <group>
      <Aandrijfunit m={m} e={e} />
      <Sensorzuil m={m} e={e} />
      <Kap m={m} e={e} />
      <Mast m={m} binnen={binnen} cilinder={cilinder} />
      <Wielarmen m={m} />
      <group ref={wagen}>
        <Vorkenwagen m={m} e={e} />
        {lading}
      </group>
    </group>
  )
}
